import React from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { euro } from "../../utils/format";
import PlantImage from "./PlantImage";

const FREE_DELIVERY_THRESHOLD = 60;

export default function CartDrawer() {
  const { lines, isOpen, closeCart, setQty, removeItem, subtotal, count } = useCart();
  const toFree = Math.max(0, FREE_DELIVERY_THRESHOLD - subtotal);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 z-[60] bg-green-900/40 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
            className="fixed inset-y-0 right-0 z-[70] flex w-full max-w-md flex-col bg-canvas shadow-lift"
            role="dialog"
            aria-label="Shopping cart"
          >
            <header className="flex items-center justify-between border-b border-line px-5 py-4">
              <h2 className="font-display text-xl">Your basket {count > 0 && `(${count})`}</h2>
              <button onClick={closeCart} aria-label="Close cart" className="p-1 text-ink-soft hover:text-ink">
                <X size={22} />
              </button>
            </header>

            {lines.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
                <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-forest-100 text-forest-700">
                  <ShoppingBag size={28} />
                </span>
                <h3 className="t-h3 mb-1">Nothing in here yet</h3>
                <p className="mb-6 text-ink-soft">A glasshouse starts with one plant.</p>
                <Link to="/shop" onClick={closeCart} className="btn-forest">
                  Browse the shop
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {subtotal < FREE_DELIVERY_THRESHOLD && (
                    <div className="mb-4 rounded-xl bg-forest-100 px-4 py-3 text-sm text-forest-900">
                      Add <strong>{euro(toFree)}</strong> more for free delivery.
                    </div>
                  )}
                  <ul className="space-y-4">
                    {lines.map((l) => (
                      <li key={l.id} className="flex gap-3">
                        <Link to={`/shop/${l.slug}`} onClick={closeCart} className="w-20 shrink-0">
                          <PlantImage src={l.image} alt={l.name} ratio="1/1" className="rounded-xl" />
                        </Link>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <Link
                              to={`/shop/${l.slug}`}
                              onClick={closeCart}
                              className="font-display text-base leading-tight hover:text-forest-700"
                            >
                              {l.name}
                            </Link>
                            <button
                              onClick={() => removeItem(l.id)}
                              aria-label={`Remove ${l.name}`}
                              className="text-ink-soft hover:text-danger"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <p className="t-price mt-0.5 text-base">{euro(l.price)}</p>
                          <div className="mt-2 flex items-center gap-3">
                            <div className="flex items-center rounded-full border border-line">
                              <button
                                onClick={() => setQty(l.id, l.qty - 1)}
                                aria-label="Decrease quantity"
                                className="px-2.5 py-1.5 text-ink-soft hover:text-ink"
                              >
                                <Minus size={14} />
                              </button>
                              <span className="min-w-[1.5rem] text-center text-sm font-medium">{l.qty}</span>
                              <button
                                onClick={() => setQty(l.id, l.qty + 1)}
                                disabled={l.qty >= l.stock}
                                aria-label="Increase quantity"
                                className="px-2.5 py-1.5 text-ink-soft hover:text-ink disabled:opacity-40"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                            {l.qty >= l.stock && (
                              <span className="text-xs text-terracotta">Max stock</span>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <footer className="border-t border-line px-5 py-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-ink-soft">Subtotal</span>
                    <span className="t-price text-xl">{euro(subtotal)}</span>
                  </div>
                  <p className="mb-3 text-xs text-ink-soft">
                    Delivery &amp; click-and-collect calculated at checkout.
                  </p>
                  <Link to="/checkout" onClick={closeCart} className="btn-brass w-full">
                    Go to checkout
                  </Link>
                  <button onClick={closeCart} className="btn-ghost mt-1 w-full">
                    Keep browsing
                  </button>
                </footer>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
